import { useRouter } from 'next/router';
import Menu from 'epn-ui/umd/dist/components/Menu';
import type { MenuProps } from 'antd/es/menu';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useI18n, { LocaleTypes } from '@i18n';

import routes from '@config/routes.json';

import BaseContainer from '@base/BaseContainer';
import BaseGrid from '@base/BaseGrid';
import BaseSemanticLink from '@base/BaseSemanticLink';
import Header from './Header';
import Footer from './Footer';

import { ILayoutProps } from './default';
import { ITabPanelItem, ScreenTypes } from './TabPanel/interfaces';

const oldCabinetLinks = JSON.parse(process.env.NEXT_PUBLIC_OLD_CABINET_LINKS);
type MenuItem = Required<MenuProps>['items'][number];

const FaqLayout: React.FC<ILayoutProps> = ({ children, isAuth }) => {
    const { t, locale } = useI18n('Faq');
    const { pathname } = useRouter();
    const page = pathname.split('/')[3];
    const items: MenuItem[] = [
        {
            label: (
                <BaseSemanticLink
                    to={oldCabinetLinks.help}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('Where to begin?')}
                </BaseSemanticLink>
            ),
            key: 'help',
        },
        {
            label: (
                <BaseSemanticLink
                    to={oldCabinetLinks.tools}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('Tools')}
                </BaseSemanticLink>
            ),
            key: 'tools',
        },
        {
            label: (
                <BaseSemanticLink
                    to={oldCabinetLinks.stats}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('Statistics')}
                </BaseSemanticLink>
            ),
            key: 'stats',
        },
        {
            label: (
                <BaseSemanticLink
                    to={oldCabinetLinks.purses}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('Purses and withdrawal of money')}
                </BaseSemanticLink>
            ),
            key: 'purses',
        },
        {
            label: (
                <BaseSemanticLink
                    to={oldCabinetLinks.problems}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('Why did not the order be credited?')}
                </BaseSemanticLink>
            ),
            key: 'problems',
        },
        {
            label: (
                <BaseSemanticLink
                    to={oldCabinetLinks.faq}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('QA')}
                </BaseSemanticLink>
            ),
            key: 'faq',
        },
        {
            label: (
                <BaseSemanticLink
                    to={routes.faq.selfEmployed}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('SelfEmployed')}
                </BaseSemanticLink>
            ),
            key: 'selfEmployed',
        },
        {
            label: (
                <BaseSemanticLink
                    to={oldCabinetLinks.telegramBot}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('Telegram-bot')}
                </BaseSemanticLink>
            ),
            key: 'telegramBot',
        },
    ];

    if (locale === LocaleTypes.ru) {
        items.push({
            label: (
                <BaseSemanticLink
                    to={routes.faq.learning}
                    className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                >
                    {t('Learning')}
                </BaseSemanticLink>
            ),
            key: 'learning',
        });

        items.push({
            label: (
                <p className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800">
                    Продвижение
                </p>
            ),
            key: 'promotion',
            icon: <FontAwesomeIcon icon={faChevronLeft} style={{ color: '#64748b' }} />,
            expandIcon: false,
            type: 'submenu',
            children: [
                {
                    label: (
                        <a
                            href="https://epn.bz/info/kak-vybrat-temu-dlya-bloga/"
                            className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                        >
                            Как выбрать нишу ...
                        </a>
                    ),
                    key: '',
                },
                {
                    label: (
                        <a
                            href="https://epn.bz/info/o-chem-pisat-v-bloge/"
                            className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                        >
                            Как наполнять блог ...
                        </a>
                    ),
                    key: '',
                },
                {
                    label: (
                        <a
                            href="https://epn.bz/info/kak-popast-v-rekomendacii-instagram/"
                            className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                        >
                            Способы взаимодействия ...
                        </a>
                    ),
                    key: '',
                },
                {
                    label: (
                        <a
                            href="https://epn.bz/info/planirovanie-reklamnoj-kampanii/"
                            className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                        >
                            Подготовка площадок ...
                        </a>
                    ),
                    key: '',
                },
                {
                    label: (
                        <a
                            href="https://epn.bz/info/kak-obojti-bany-v-facebook/"
                            className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                        >
                            Реклама в соцсетях ...
                        </a>
                    ),
                    key: '',
                },
                {
                    label: (
                        <a
                            href="https://epn.bz/info/kak-nastroit-reklamu-v-yandekse-i-google/"
                            className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                        >
                            Рекламные кабинеты ...
                        </a>
                    ),
                    key: '',
                },
                {
                    label: (
                        <a
                            href="https://epn.bz/info/reklama-u-blogerov/"
                            className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                        >
                            Реклама у блогеров ...
                        </a>
                    ),
                    key: '',
                },
                {
                    label: (
                        <a
                            href="https://epn.bz/info/osnovy-seo/"
                            className="!text-blueGray-500 !text-[16px] hover:!text-blueGray-800"
                        >
                            Базовые параметры SEO ...
                        </a>
                    ),
                    key: '',
                },
            ],
        });
    }
    const tabList: ITabPanelItem[] = [
        {
            title: t('Where to begin?'),
            href: oldCabinetLinks.help,
            screen: ScreenTypes.always,
            active: !page,
        },
        {
            title: t('Tools'),
            href: oldCabinetLinks.tools,
            screen: ScreenTypes.always,
            active: page === 'tools',
        },
        {
            title: t('Statistics'),
            href: oldCabinetLinks.stats,
            screen: ScreenTypes.xs,
            active: page === 'stats',
        },
        {
            title: t('Purses and withdrawal of money'),
            href: oldCabinetLinks.purses,
            screen: ScreenTypes.sm,
            active: page === 'purses',
        },
        {
            title: t('Why did not the order be credited?'),
            href: oldCabinetLinks.problems,
            screen: ScreenTypes.md,
            active: page === 'problems',
        },
        {
            title: t('QA'),
            href: oldCabinetLinks.faq,
            screen: ScreenTypes.xl,
            active: page === 'faq',
        },
        {
            title: t('SelfEmployed'),
            href: routes.faq.selfEmployed,
            screen: ScreenTypes.xl,
            active: page === 'self-employed',
        },
        {
            title: t('Telegram-bot'),
            href: oldCabinetLinks.telegramBot,
            screen: ScreenTypes.never,
            active: page === 'telegram-bot',
        },
    ];

    if (locale === LocaleTypes.ru) {
        tabList.push({
            title: t('Learning'),
            href: routes.faq.learning,
            screen: ScreenTypes.never,
            active: page === 'learning',
        });

        tabList.push({
            title: 'Продвижение',
            href: '',
            screen: ScreenTypes.never,
            active: false,
            childrens: [
                {
                    title: 'Как выбрать нишу ...',
                    href: 'https://epn.bz/info/kak-vybrat-temu-dlya-bloga/',
                    screen: ScreenTypes.never,
                },
                {
                    title: 'Как наполнять блог ...',
                    href: 'https://epn.bz/info/o-chem-pisat-v-bloge/',
                    screen: ScreenTypes.never,
                },
                {
                    title: 'Способы взаимодействия ...',
                    href: 'https://epn.bz/info/kak-popast-v-rekomendacii-instagram/',
                    screen: ScreenTypes.never,
                },
                {
                    title: 'Подготовка площадок ...',
                    href: 'https://epn.bz/info/planirovanie-reklamnoj-kampanii/',
                    screen: ScreenTypes.never,
                },
                {
                    title: 'Реклама в соцсетях ...',
                    href: 'https://epn.bz/info/kak-obojti-bany-v-facebook/',
                    screen: ScreenTypes.never,
                },
                {
                    title: 'Рекламные кабинеты ...',
                    href: 'https://epn.bz/info/kak-nastroit-reklamu-v-yandekse-i-google/',
                    screen: ScreenTypes.never,
                },
                {
                    title: 'Реклама у блогеров ...',
                    href: 'https://epn.bz/info/reklama-u-blogerov/',
                    screen: ScreenTypes.never,
                },
                {
                    title: 'Базовые параметры SEO ...',
                    href: 'https://epn.bz/info/osnovy-seo/',
                    screen: ScreenTypes.never,
                },
            ],
        });
    }

    return (
        <>
            <Header isAuth={isAuth} />
            {/* <TabPanel tabList={tabList} /> */}

            <div className="mb-10 border-b border-solid border-blueGray-200">
                <div className="relative h-full mx-auto box-border xs:w-full sm:w-full md:container-w-md lg:container-w-lg xl:container-w-xl">
                    <Menu mode="horizontal" items={items} style={{ borderBottom: 0 }} />
                </div>
            </div>
            {/* хак: обертка в лишний див для фикса тк меняется класс на toastify */}
            <div>
                <BaseContainer>
                    <BaseGrid container>{children}</BaseGrid>
                </BaseContainer>
            </div>
            <Footer isAuth={isAuth} />
        </>
    );
};

export default FaqLayout;
